import { faSquare } from "@fortawesome/pro-light-svg-icons/faSquare";
import {
  faArrowDown,
  faCheck,
  faChevronDown,
  faCircle,
  faMinus,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Box,
  CssVarsTheme,
  extendTheme,
  alertClasses,
  formControlLabelClasses,
  formHelperTextClasses,
  formLabelClasses,
  tableRowClasses,
  ThemeOptions,
} from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { buttonClasses } from "@mui/material/Button";
import { buttonGroupClasses } from "@mui/material/ButtonGroup";
import { checkboxClasses } from "@mui/material/Checkbox";
import { chipClasses } from "@mui/material/Chip";
import { filledInputClasses } from "@mui/material/FilledInput";
import { inputBaseClasses } from "@mui/material/InputBase";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { radioClasses } from "@mui/material/Radio";
import { selectClasses } from "@mui/material/Select";
import { svgIconClasses } from "@mui/material/SvgIcon";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { switchClasses } from "@mui/material/Switch";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE,
  FA_ICON_DEFAULT_WIDTH_CSS_VARIABLE,
  FaIcon,
  FONT_AWESOME_FIXED_WIDTH,
} from "../components/DataDisplay/Icon/FaIcon";
import * as dark from "./darkColors";
import { DarkColorScheme } from "./darkTheme";
import { LightColorScheme } from "./lightTheme";
import { sr25BlueOklch } from "./sr25Colors";
import { SrAutocompletePopper } from "./SrAutocompletePopper";
import { SrPopoverGrow } from "./SrPopoverGrow";

/// Converts a `px` number to `rem` units - assumes a base font size of 16px (browser defaults)
export const pxToRem = (px: number): string => `${px / 16}rem`;

const getSvgIconFontSize = (theme: CssVarsTheme, fontSize: SvgIconProps["fontSize"]) => {
  switch (fontSize) {
    case "inherit":
      return "inherit";
    case "xsmall":
      return theme.typography.svgIconXs.fontSize;
    case "small":
      return theme.typography.svgIconSm.fontSize;
    case "medium":
      return theme.typography.svgIconMd.fontSize;
    case "large":
      return theme.typography.svgIconLg.fontSize;
    case "xlarge":
      return theme.typography.svgIconXl.fontSize;
    case "xxlarge":
      return theme.typography.svgIconXxl.fontSize;
    default:
      return theme.typography.svgIcon.fontSize;
  }
};

const typography: ThemeOptions["typography"] = {
  fontFamily: "Inter",
  xxl: {
    fontWeight: 700,
    fontSize: pxToRem(48),
    lineHeight: pxToRem(56),
  },
  xl: {
    fontWeight: 700,
    fontSize: pxToRem(32),
    lineHeight: pxToRem(48),
  },
  h1: {
    fontWeight: 700,
    fontSize: pxToRem(24),
    lineHeight: "1.5",
    letterSpacing: "-0.01em",
  },
  h2: {
    fontWeight: 700,
    fontSize: pxToRem(20),
    lineHeight: "1.5",
    letterSpacing: "-0.01em",
  },
  h3: {
    fontWeight: 700,
    fontSize: pxToRem(18),
    lineHeight: "1.5",
  },
  h4: {
    fontWeight: 600,
    fontSize: pxToRem(16),
    lineHeight: "1.5",
  },
  h5: {
    fontWeight: 600,
    fontSize: pxToRem(15),
    lineHeight: "1.5",
  },
  h6: {
    fontWeight: 600,
    fontSize: pxToRem(13),
    lineHeight: "1.5",
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: pxToRem(18),
    lineHeight: pxToRem(24),
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: pxToRem(14),
    lineHeight: pxToRem(20),
  },
  body1: {
    fontWeight: 400,
    fontSize: pxToRem(15),
    lineHeight: pxToRem(24),
  },
  body2: {
    fontWeight: 400,
    fontSize: pxToRem(13),
    lineHeight: pxToRem(19),
  },
  overline: {
    fontWeight: 500,
    fontSize: pxToRem(12),
    lineHeight: pxToRem(18),
  },
  helperText: {
    fontWeight: 400,
    fontSize: pxToRem(13),
    lineHeight: pxToRem(20),
  },
  caption: {
    fontWeight: 400,
    fontSize: pxToRem(12),
    lineHeight: pxToRem(18),
  },
  inputLabel: {
    fontWeight: 500,
    fontSize: pxToRem(15),
    lineHeight: pxToRem(20),
  },
  inputText: {
    fontWeight: 400,
    fontSize: pxToRem(15),
    lineHeight: pxToRem(20),
  },
  inputTextLarge: {
    fontWeight: 400,
    fontSize: pxToRem(17),
    lineHeight: pxToRem(32),
  },
  buttonTextLarge: {
    fontWeight: 700,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(26),
  },
  buttonTextMedium: {
    fontWeight: 600,
    fontSize: pxToRem(14),
    lineHeight: pxToRem(20),
  },
  buttonTextSmall: {
    fontWeight: 600,
    fontSize: pxToRem(13),
    lineHeight: pxToRem(20),
  },
  svgIconXs: {
    fontSize: pxToRem(8),
  },
  svgIconSm: {
    fontSize: pxToRem(16),
  },
  svgIcon: {
    fontSize: pxToRem(16),
  },
  svgIconMd: {
    fontSize: pxToRem(18),
  },
  svgIconLg: {
    fontSize: pxToRem(24),
  },
  svgIconXl: {
    fontSize: pxToRem(48),
  },
  svgIconXxl: {
    fontSize: pxToRem(60),
  },
};

const getSelectionControlSelectors = ({
  checkedClasses,
  disabledClass,
  rootClass,
}: {
  checkedClasses: string[];
  disabledClass: string;
  rootClass: string;
}) => {
  const labelHover = `.${formControlLabelClasses.root}:has(> .${rootClass}):hover &`;
  const labelActive = `.${formControlLabelClasses.root}:has(> .${rootClass}):active &`;
  const checkedSelector = checkedClasses.map((checkedClass) => `&.${checkedClass}`).join(", ");

  return {
    active: `&:active, ${labelActive}`,
    checked: checkedSelector,
    checkedActive: checkedClasses
      .map((checkedClass) => `&.${checkedClass}:active, ${labelActive}.${checkedClass}`)
      .join(", "),
    checkedDisabledHover: checkedClasses
      .map(
        (checkedClass) =>
          `&.${checkedClass}.${disabledClass}:hover, ${labelHover}.${checkedClass}.${disabledClass}`,
      )
      .join(", "),
    checkedHover: checkedClasses
      .map((checkedClass) => `&.${checkedClass}:hover, ${labelHover}.${checkedClass}`)
      .join(", "),
    disabled: `&.${disabledClass}`,
    focusVisible: `&.${rootClass}:has(:focus-visible)`,
    hover: `&:hover, ${labelHover}`,
    interaction: `&:hover, &:active, ${labelHover}, ${labelActive}`,
  };
};

const getInputBaseSizeConfigs = (theme: CssVarsTheme) => [
  {
    size: "small" as const,
    minHeight: pxToRem(36),
    // A small Chip is 24px tall. The InputBase minimum height centers shorter
    // text content while this padding keeps one chip within the same 36px field.
    selectPaddingBlock: pxToRem((36 - 24) / 2),
    paddingTop: pxToRem(6),
    paddingBottom: pxToRem(7),
    typography: theme.typography.inputText,
  },
  {
    size: "medium" as const,
    minHeight: pxToRem(42),
    selectPaddingBlock: pxToRem((42 - 32) / 2),
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8.5),
    typography: theme.typography.inputTextLarge,
  },
];

const getInputBaseSizeVariants = (
  theme: CssVarsTheme,
  getStyle: (config: ReturnType<typeof getInputBaseSizeConfigs>[number]) => Record<string, unknown>,
) =>
  getInputBaseSizeConfigs(theme).map((config) => ({
    props: { size: config.size },
    style: getStyle(config),
  }));

export const THEME_MODE_STORAGE_KEY = "Shuttlerock:ThemeMode";
export const THEME_SCHEME_STORAGE_KEY = "Shuttlerock:ColorScheme";

export const SrTheme = extendTheme({
  cssVarPrefix: "sr",
  // MUI's default ladder (1000–1500) rebased around the shadcn overlays, which sit at
  // z-index 50 in `<body>` (@shuttlerock/ui). Everything both libraries portal to
  // `<body>` must interleave: a MUI modal (30) under a Base UI popup opened inside it
  // (50), under a MUI tooltip (70), under the notistack stack (1400, notistack's own
  // constant). Elements inside `#root` keep their order among themselves; the root
  // is an isolated stacking context, so their values never meet the portals' anyway.
  // Deleted with the last MUI surface.
  zIndex: {
    mobileStepper: 10,
    fab: 15,
    speedDial: 15,
    appBar: 20,
    drawer: 25,
    modal: 30,
    snackbar: 60,
    tooltip: 70,
  },
  shape: {
    borderRadius: 8,
    borderRadiusXs: 4,
    borderRadiusSm: 6,
    borderRadiusMd: 8,
    borderRadiusLg: 10,
    borderRadiusXl: 12,
    borderRadius2Xl: 14,
    borderRadiusMax: 100,
  },
  typography,
  transitions: {
    duration: {
      interactiveStateIn: 50,
      interactiveStateOut: 200,
    },
    easing: {
      easeOutCirc: "cubic-bezier(0, 0.91, 0, 1)",
      easeInCirc: "cubic-bezier(0.59, 0, 1, 0.375)",
    },
  },
  colorSchemes: {
    light: LightColorScheme,
    dark: DarkColorScheme,
  },
  colorSchemeSelector: "data",
  shadows: [
    "none",
    "0px 2px 1px -1px rgba(17,17,17,0.05),0px 1px 1px 0px rgba(17,17,17,0.02),0px 1px 7px 0px rgba(17,17,17,0.08)",
    "0px 3px 1px -2px rgba(17,17,17,0.05),0px 2px 2px 0px rgba(17,17,17,0.02),0px 1px 9px 0px rgba(17,17,17,0.08)",
    "0px 3px 3px -2px rgba(17,17,17,0.05),0px 3px 4px 0px rgba(17,17,17,0.02),0px 1px 12px 0px rgba(17,17,17,0.08)",
    "0px 2px 4px -1px rgba(17,17,17,0.05),0px 4px 5px 0px rgba(17,17,17,0.02),0px 1px 14px 0px rgba(17,17,17,0.08)",
    "0px 3px 5px -1px rgba(17,17,17,0.05),0px 5px 8px 0px rgba(17,17,17,0.02),0px 1px 18px 0px rgba(17,17,17,0.08)",
    "0px 3px 5px -1px rgba(17,17,17,0.05),0px 6px 10px 0px rgba(17,17,17,0.02),0px 1px 22px 0px rgba(17,17,17,0.08)",
    "0px 4px 5px -2px rgba(17,17,17,0.05),0px 7px 10px 1px rgba(17,17,17,0.02),0px 2px 20px 1px rgba(17,17,17,0.08)",
    "0px 5px 5px -3px rgba(17,17,17,0.05),0px 8px 10px 1px rgba(17,17,17,0.02),0px 3px 18px 2px rgba(17,17,17,0.08)",
    "0px 5px 6px -3px rgba(17,17,17,0.05),0px 9px 12px 1px rgba(17,17,17,0.02),0px 3px 20px 2px rgba(17,17,17,0.08)",
    "0px 6px 6px -3px rgba(17,17,17,0.05),0px 10px 14px 1px rgba(17,17,17,0.02),0px 4px 22px 3px rgba(17,17,17,0.08)",
    "0px 6px 7px -4px rgba(17,17,17,0.05),0px 11px 15px 1px rgba(17,17,17,0.02),0px 4px 24px 3px rgba(17,17,17,0.08)",
    "0px 7px 8px -4px rgba(17,17,17,0.05),0px 12px 17px 2px rgba(17,17,17,0.02),0px 5px 26px 4px rgba(17,17,17,0.08)",
    "0px 7px 8px -4px rgba(17,17,17,0.05),0px 13px 19px 2px rgba(17,17,17,0.02),0px 5px 28px 4px rgba(17,17,17,0.08)",
    "0px 7px 9px -4px rgba(17,17,17,0.05),0px 14px 21px 2px rgba(17,17,17,0.02),0px 5px 30px 4px rgba(17,17,17,0.08)",
    "0px 8px 9px -5px rgba(17,17,17,0.05),0px 15px 22px 2px rgba(17,17,17,0.02),0px 6px 32px 5px rgba(17,17,17,0.08)",
    "0px 8px 10px -5px rgba(17,17,17,0.05),0px 16px 24px 2px rgba(17,17,17,0.02),0px 6px 34px 5px rgba(17,17,17,0.08)",
    "0px 8px 11px -5px rgba(17,17,17,0.05),0px 17px 26px 2px rgba(17,17,17,0.02),0px 6px 36px 5px rgba(17,17,17,0.08)",
    "0px 9px 11px -5px rgba(17,17,17,0.05),0px 18px 28px 2px rgba(17,17,17,0.02),0px 7px 38px 6px rgba(17,17,17,0.08)",
    "0px 9px 12px -6px rgba(17,17,17,0.05),0px 19px 29px 2px rgba(17,17,17,0.02),0px 7px 40px 6px rgba(17,17,17,0.08)",
    "0px 10px 13px -6px rgba(17,17,17,0.05),0px 20px 31px 3px rgba(17,17,17,0.02),0px 8px 42px 7px rgba(17,17,17,0.08)",
    "0px 10px 13px -6px rgba(17,17,17,0.05),0px 21px 33px 3px rgba(17,17,17,0.02),0px 8px 44px 7px rgba(17,17,17,0.08)",
    "0px 10px 14px -6px rgba(17,17,17,0.05),0px 22px 35px 3px rgba(17,17,17,0.02),0px 8px 46px 7px rgba(17,17,17,0.08)",
    "0px 11px 14px -7px rgba(17,17,17,0.05),0px 23px 36px 3px rgba(17,17,17,0.02),0px 9px 48px 8px rgba(17,17,17,0.08)",
    "0px 11px 15px -7px rgba(17,17,17,0.05),0px 24px 38px 3px rgba(17,17,17,0.02),0px 9px 50px 8px rgba(17,17,17,0.08)",
  ],
  components: {
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderWidth: "2px",
          "& .MuiAlert-icon": {
            [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          },
          [`&.${alertClasses.standard}`]: {
            [`&.${alertClasses.colorError}`]: {
              backgroundColor: theme.vars.palette.Alert.errorStandardBg,
              color: theme.vars.palette.Alert.errorStandardColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.errorStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.${alertClasses.colorInfo}`]: {
              backgroundColor: theme.vars.palette.Alert.infoStandardBg,
              color: theme.vars.palette.Alert.infoStandardColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.infoStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.${alertClasses.colorSuccess}`]: {
              backgroundColor: theme.vars.palette.Alert.successStandardBg,
              color: theme.vars.palette.Alert.successStandardColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.successStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.${alertClasses.colorWarning}`]: {
              backgroundColor: theme.vars.palette.Alert.warningStandardBg,
              color: theme.vars.palette.Alert.warningStandardColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.warningStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorSecondary`]: {
              backgroundColor: theme.vars.palette.Alert.secondaryStandardBg,
              color: theme.vars.palette.Alert.secondaryStandardColor,
              "& .MuiAlertTitle-root": {
                color: theme.vars.palette.Alert.secondaryStandardTitleColor,
              },
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.secondaryStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorAutomation`]: {
              backgroundColor: theme.vars.palette.Alert.automationStandardBg,
              color: theme.vars.palette.Alert.automationStandardColor,
              "& .MuiAlertTitle-root": {
                color: theme.vars.palette.Alert.automationStandardTitleColor,
              },
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.automationStandardIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorInstructions`]: {
              background: theme.vars.palette.Alert.instructionsStandardBg,
              border: theme.vars.palette.Alert.instructionsStandardBorder,
              color: theme.vars.palette.Alert.instructionsStandardColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.instructionsStandardIconColor,
                opacity: "initial",
              },
              "& .MuiAlert-message": {
                color: theme.vars.palette.Alert.instructionsStandardColor,
                opacity: "initial",
              },
              "& .MuiAlert-action": {
                color: theme.vars.palette.Alert.instructionsStandardActionColor,
                opacity: "initial",
              },
            },
          },

          [`&.${alertClasses.filled}`]: {
            [`&.MuiAlert-colorAutomation`]: {
              background: theme.vars.palette.Alert.automationFilledBg,
              color: theme.vars.palette.Alert.automationFilledColor,
            },
            [`&.MuiAlert-colorSecondary`]: {
              background: theme.vars.palette.Alert.secondaryFilledBg,
              color: theme.vars.palette.Alert.secondaryFilledColor,
            },
            [`&.MuiAlert-colorInstructions`]: {
              background: theme.vars.palette.Alert.instructionsFilledBg,
              color: theme.vars.palette.Alert.instructionsFilledColor,
            },
          },

          [`&.${alertClasses.outlined}`]: {
            [`&.${alertClasses.colorInfo}`]: {
              backgroundColor: theme.vars.palette.Alert.infoOutlinedBg,
              borderColor: theme.vars.palette.Alert.infoOutlinedBorder,
              color: theme.vars.palette.Alert.infoOutlinedColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.infoOutlinedIconColor,
                opacity: "initial",
              },
            },

            [`&.${alertClasses.colorSuccess}`]: {
              backgroundColor: theme.vars.palette.Alert.successOutlinedBg,
              borderColor: theme.vars.palette.Alert.successOutlinedBorder,
              color: theme.vars.palette.Alert.successOutlinedColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.successOutlinedIconColor,
                opacity: "initial",
              },
            },

            [`&.${alertClasses.colorWarning}`]: {
              backgroundColor: theme.vars.palette.Alert.warningOutlinedBg,
              borderColor: theme.vars.palette.Alert.warningOutlinedBorder,
              color: theme.vars.palette.Alert.warningOutlinedColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.warningOutlinedIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorSecondary`]: {
              borderColor: theme.vars.palette.Alert.secondaryOutlinedBorder,
              color: theme.vars.palette.Alert.secondaryOutlinedColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.secondaryOutlinedIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorAutomation`]: {
              borderColor: theme.vars.palette.Alert.automationOutlinedBorder,
              color: theme.vars.palette.Alert.automationOutlinedColor,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.automationOutlinedIconColor,
                opacity: "initial",
              },
            },

            [`&.MuiAlert-colorInstructions`]: {
              color: theme.vars.palette.Alert.instructionsOutlinedColor,
              background: theme.vars.palette.Alert.instructionsOutlinedBg,
              border: theme.vars.palette.Alert.instructionsOutlinedBorder,
              "& .MuiAlert-icon": {
                color: theme.vars.palette.Alert.instructionsOutlinedIconColor,
                opacity: "initial",
              },
              "& .MuiAlert-action": {
                color: theme.vars.palette.Alert.instructionsOutlinedActionColor,
                opacity: "initial",
              },
            },
          },
        }),
        action: {
          "& svg": {
            width: "1.5em",
            height: "1.5em",
          },
        },
        // other severity alerts still using default MUI Icon, therefore we only override the icon style for severity `info`
        icon: {
          variants: [
            {
              props: { severity: "info" },
              style: {
                fontSize: "1.25rem",
                padding: "8px 0",
              },
            },
          ],
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: "default",
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
        slots: {
          popper: SrAutocompletePopper,
        },
        slotProps: {
          clearIndicator: {
            color: "subtle",
          },
          popupIndicator: {
            color: "subtle",
          },
        },
        clearIcon: (
          <Box
            sx={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaIcon icon={faXmark} sx={{ fontSize: "0.75rem" }} />
          </Box>
        ),
        popupIcon: (
          <Box
            sx={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaIcon icon={faChevronDown} sx={{ fontSize: "0.75rem" }} />
          </Box>
        ),
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.${autocompleteClasses.root} .${autocompleteClasses.inputRoot}`]: {
            [`&.${outlinedInputClasses.root}, &.${filledInputClasses.root}`]: {
              paddingTop: pxToRem(4),
              paddingBottom: pxToRem(4),

              [`.${inputBaseClasses.input}`]: {
                paddingLeft: "6px",
              },
            },
          },
          variants: [
            {
              props: { size: "medium" },
              style: {
                input: theme.typography.inputTextLarge,
              },
            },
            {
              props: { readOnly: true },
              style: {
                [`&.${autocompleteClasses.root} .${autocompleteClasses.inputRoot}`]: {
                  [`&.${outlinedInputClasses.root}`]: {
                    paddingLeft: 0,
                    [`.${autocompleteClasses.input}`]: { paddingLeft: 0 },
                    [`.${autocompleteClasses.endAdornment}`]: {
                      display: "none",
                    },
                  },
                },
              },
            },
          ],
        }),
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Menu.bg,
          backgroundImage: "none",
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.vars.palette.Menu.boxShadow,
          color: theme.vars.palette.Menu.color,
        }),
        listbox: ({ theme }) => ({
          display: "flex",
          flexFlow: "column",
          padding: 0,
          margin: 2,
          gap: 1,
          borderRadius: theme.shape.borderRadiusSm,
          maxHeight: `min(calc(48px * 8 + 32px), 40vh)`, // 4 options with 8px gap, then scroll
        }),
        option: ({ theme }) => ({
          flex: "0 0 auto",
          borderRadius: theme.shape.borderRadiusSm,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          color: theme.vars.palette.on.surface,
          [`&.${autocompleteClasses.focused}`]: {
            backgroundColor: theme.vars.palette.surface.overlay,
          },
          [`&.${autocompleteClasses.focused}:not([aria-selected='true']):active`]: {
            backgroundColor: theme.vars.palette.surface.overlayStrong,
          },
          variants: [
            {
              props: { size: "medium" },
              style: {
                ...theme.typography.inputTextLarge,
              },
            },
          ],
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          width: "34px",
          height: "34px",
          fontWeight: 600,
          fontSize: pxToRem(12),
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          transitionProperty:
            "var(--transition-property-override, color, border-color, box-shadow, background-color)",
          transitionDuration: `var(--transition-duration-override, ${theme.transitions.duration.interactiveStateOut}ms)`,
          "&:hover": {
            transitionDuration: `var(--transition-duration-override, ${theme.transitions.duration.interactiveStateIn}ms)`,
          },
          "&:focus-visible": {
            outline: `2px solid oklch(${sr25BlueOklch[500]} / 0.5)`,
            outlineOffset: "1px",
          },
        }),
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: "none",
          [`& .${buttonClasses.root}:hover`]: {
            boxShadow: "none",
          },
          [`& .${buttonGroupClasses.firstButton}`]: {
            borderColor: theme.vars.palette.stroke.transparent,
          },
          variants: [
            {
              props: { size: "small" },
              style: {
                [`& .${buttonClasses.root}.${buttonGroupClasses.firstButton}`]: {
                  borderTopLeftRadius: pxToRem(4),
                  borderBottomLeftRadius: pxToRem(4),
                },
                [`& .${buttonClasses.root}.${buttonGroupClasses.lastButton}`]: {
                  borderTopRightRadius: pxToRem(4),
                  borderBottomRightRadius: pxToRem(4),
                },
              },
            },
          ],
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Card.bg,
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        checkedIcon: <FaIcon icon={faCheck} fontSize="inherit" />,
        indeterminateIcon: <FaIcon icon={faMinus} fontSize="inherit" />,
        icon: <FaIcon icon={faSquare} fontSize="inherit" sx={{ opacity: 0 }} />,
      },
      variants: [
        {
          props: { size: "large" },
          style: ({ theme }) => ({
            width: "24px",
            height: "24px",
            fontSize: pxToRem(16),

            [theme.breakpoints.down("sm")]: {
              width: "28px",
              height: "28px",
              fontSize: pxToRem(24),
            },
          }),
        },
      ],
      styleOverrides: {
        root: ({ theme }) => {
          const selectors = getSelectionControlSelectors({
            checkedClasses: [checkboxClasses.checked, checkboxClasses.indeterminate],
            disabledClass: checkboxClasses.disabled,
            rootClass: checkboxClasses.root,
          });
          const checkedStateStyles = {
            backgroundColor: theme.vars.palette.primary.main,
            borderColor: theme.vars.palette.primary.main,
            color: theme.vars.palette.on.primary,
            [`& + .${formControlLabelClasses.label}`]: {
              color: theme.vars.palette.Checkbox.checkedLabelColor,
            },
          };

          return {
            color: theme.vars.palette.Checkbox.color,
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            margin: 5,
            border: `1px solid ${theme.vars.palette.stroke.main}`,
            width: "20px",
            height: "20px",
            fontSize: pxToRem(12),
            "--transition-property-override": "border-color",

            [theme.breakpoints.down("sm")]: {
              width: "24px",
              height: "24px",
              fontSize: pxToRem(16),
            },

            [selectors.focusVisible]: {
              outline: `2px solid oklch(${sr25BlueOklch[500]} / 0.5)`,
              outlineOffset: "1px",
            },

            [selectors.checked]: checkedStateStyles,

            [selectors.interaction]: {
              "--transition-duration-override": `${theme.transitions.duration.interactiveStateIn}ms`,
            },

            [selectors.hover]: {
              borderColor: theme.vars.palette.stroke.hover,
            },

            [selectors.active]: {
              borderColor: theme.vars.palette.stroke.active,
            },

            [selectors.checkedHover]: {
              borderColor: theme.vars.palette.primary.main,
            },

            [selectors.checkedActive]: {
              backgroundColor: theme.vars.palette.primary.dark,
              borderColor: theme.vars.palette.primary.dark,
            },

            [selectors.checkedDisabledHover]: {
              borderColor: theme.vars.palette.primary.main,
            },

            [selectors.disabled]: {
              opacity: 0.5,
              cursor: "default",
            },

            variants: [
              {
                props: { readOnly: true },
                style: {
                  cursor: "default",
                  [selectors.hover]: {
                    borderColor: theme.vars.palette.stroke.main,
                  },
                  [selectors.active]: {
                    borderColor: theme.vars.palette.stroke.main,
                  },
                  [selectors.checkedHover]: {
                    borderColor: theme.vars.palette.primary.main,
                  },
                  [selectors.checkedActive]: {
                    backgroundColor: theme.vars.palette.primary.main,
                    borderColor: theme.vars.palette.primary.main,
                  },
                },
              },
            ],
          };
        },
        sizeSmall: ({ theme }) => ({
          width: "16px",
          height: "16px",
          fontSize: pxToRem(12),

          [theme.breakpoints.down("sm")]: {
            width: "20px",
            height: "20px",
            fontSize: pxToRem(16),
          },
        }),
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
      variants: [
        {
          props: { size: "xsmall" },
          style: ({ theme }) => ({
            borderRadius: 0,
            height: "unset",
            ...theme.typography.caption,
          }),
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({
            [theme.breakpoints.up("md")]: {
              padding: `0 ${theme.spacing(2)}`,
            },
          }),
        },
        {
          props: { size: "medium" },
          style: ({ theme }) => ({
            [theme.breakpoints.up("md")]: {
              padding: `0 ${theme.spacing(2)}`,
            },
          }),
        },
        {
          props: { color: "gradient", variant: "filled" },
          style: ({ theme }) => ({
            background: theme.vars.palette.gradient.main,
          }),
        },
        {
          props: { color: "gradient", variant: "m3" },
          style: ({ theme }) => ({
            background: theme.vars.palette.gradient.main,
          }),
        },
        {
          // Styled to look like Material 3 chips: https://m3.material.io/components/chips/specs
          props: { variant: "m3" },
          style: ({ theme }) => ({
            [`.${chipClasses.icon}`]: {
              marginLeft: 0,
              marginRight: "8px",
              paddingLeft: 0,
            },
            [`.${chipClasses.label}`]: {
              padding: 0,
            },
            borderRadius: "4px",
            padding: "0 8px",
            [theme.breakpoints.up("sm")]: {
              padding: "0 8px",
            },
          }),
        },
        {
          props: { variant: "m3", clickable: true },
          style: ({ theme }) => ({
            "&:hover::after": {
              position: "absolute",
              inset: 0,
              content: '""',
              borderRadius: "4px",
              backgroundColor: theme.vars.palette.Chip.m3ClickableBg,
            },
          }),
        },
        {
          props: { variant: "m3", size: "small" },
          style: {
            height: "22px",
            fontSize: "0.75rem",
            [`.${chipClasses.icon}`]: {
              fontSize: "0.75rem",
            },
          },
        },
        {
          props: { variant: "m3", color: "success" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3SuccessColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3SuccessHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "warning" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3WarningColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3WarningHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "error" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3ErrorColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3ErrorHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "secondary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3SecondaryColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3SecondaryHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "default" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3DefaultColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3DefaultHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "primary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3PrimaryColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3PrimaryHoverBg,
            },
          }),
        },
        {
          props: { variant: "m3", color: "info" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Chip.m3InfoColor,
            "&, &:hover": {
              backgroundColor: theme.vars.palette.Chip.m3InfoHoverBg,
            },
          }),
        },
      ],
      styleOverrides: {
        root: ({ theme }) => ({
          [`.${chipClasses.icon}`]: {
            marginRight: "1px",
            paddingLeft: "4px",
          },
          variants: [
            {
              props: { color: "gradient", variant: "square" },
              style: {
                background: theme.vars.palette.gradient.main,
              },
            },
          ],
        }),
        filled: ({ theme }) => ({
          variants: [
            {
              props: { color: "success" },
              style: {
                color: theme.vars.palette.Chip.filledSuccessColor,
                backgroundColor: theme.vars.palette.Chip.filledSuccessBg,
              },
            },
            {
              props: { color: "warning" },
              style: {
                color: theme.vars.palette.Chip.filledWarningColor,
                backgroundColor: theme.vars.palette.Chip.filledWarningBg,
              },
            },
            {
              props: { color: "error" },
              style: {
                color: theme.vars.palette.Chip.filledErrorColor,
                backgroundColor: theme.vars.palette.Chip.filledErrorBg,
              },
            },
            {
              props: { color: "secondary" },
              style: {
                color: theme.vars.palette.Chip.filledSecondaryColor,
                backgroundColor: theme.vars.palette.Chip.filledSecondaryBg,
              },
            },
            {
              props: { color: "default", size: "small" },
              style: {
                color: theme.vars.palette.Chip.filledDefaultColor,
                backgroundColor: theme.vars.palette.Chip.filledDefaultBg,
              },
            },
            {
              props: { color: "default", size: "medium" },
              style: {
                color: theme.vars.palette.Chip.filledDefaultColor,
                backgroundColor: theme.vars.palette.Chip.filledDefaultBg,
              },
            },
            {
              props: { color: "default", size: "xsmall" },
              style: {
                color: theme.vars.palette.Chip.xsFilledDefaultColor,
                backgroundColor: theme.vars.palette.Chip.xsFilledDefaultBg,
              },
            },
          ],
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // These are required for Japanese text to use proportional characters instead of fixed-width characters
        // for better legibility and fitting in UI text within smaller spaces, for applicable fonts.
        // It doesn't cause any issues for other languages so we can safely apply it globally
        html: {
          fontFeatureSettings: '"palt" 1',
        },
        body: {
          fontFeatureSettings: '"palt" 1',
        },
        button: {
          fontFeatureSettings: '"palt" 1',
        },
        input: {
          fontFeatureSettings: '"palt" 1',
        },
        textarea: {
          fontFeatureSettings: '"palt" 1',
        },
        "::selection": ({ theme }: { theme: CssVarsTheme }) => ({
          backgroundColor: theme.vars.palette.selection,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: "16px",
          background: theme.vars.palette.Dialog.bg,
        }),
        paperFullScreen: {
          borderRadius: "unset",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.Dialog.color,
        }),
      },
    },
    MuiDialogActions: {
      defaultProps: { disableSpacing: true },
      styleOverrides: {
        root: ({ theme }) => ({
          gap: theme.spacing(1),
          flexDirection: "column",
          alignItems: "unset",
          padding: theme.spacing(2),
          paddingBottom: theme.spacing(3),
          [theme.breakpoints.up("sm")]: {
            gap: theme.spacing(1.5),
            padding: theme.spacing(3),
            flexDirection: "row",
            alignItems: "center",
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ...typography.h2,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          "& .MuiLinearProgress-barColorSuccess": {
            backgroundColor: theme.vars.palette.LinearProgress.successBarColor,
          },
          "& .MuiLinearProgress-barColorError": {
            backgroundColor: theme.vars.palette.LinearProgress.errorBarColor,
          },
        }),
        bar: ({ theme }) => ({ borderRadius: theme.shape.borderRadius }),
        barColorPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.LinearProgress.primaryBarColor,
        }),
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.LinearProgress.primaryColor,
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
      defaultProps: {
        underline: "none",
      },
    },
    MuiMenu: {
      defaultProps: {
        slots: {
          transition: SrPopoverGrow,
        },
        elevation: 0,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Menu.bg,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.vars.palette.Menu.boxShadow,
          color: theme.vars.palette.Menu.color,
        }),
        root: ({ theme }) => ({
          "& .MuiList-root": {
            display: "flex",
            flexFlow: "column",
            padding: 0,
            margin: 2,
            gap: 1,
          },
          "& .MuiDivider-root": {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
          },
        }),
      },
    },
    MuiPopover: {
      defaultProps: {
        slots: {
          transition: SrPopoverGrow,
        },
        elevation: 0,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Menu.bg,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.vars.palette.Menu.boxShadow,
          color: theme.vars.palette.Menu.color,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadiusSm,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          color: theme.vars.palette.on.surface,
          "&:active": {
            backgroundColor: theme.vars.palette.surface.overlayStrong,
          },
          "& .MuiListItemIcon-root": {
            color: "inherit",
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          // Descendant FaIcon instances use this only when automaticWidth is omitted.
          [FA_ICON_DEFAULT_WIDTH_CSS_VARIABLE]: FONT_AWESOME_FIXED_WIDTH,
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: (props) => <FaIcon icon={faArrowDown} {...props} />,
      },
      styleOverrides: {
        root: {
          width: "100%",
          justifyContent: "space-between",
          ".MuiTableSortLabel-icon": { fontSize: "0.75em" },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { size: "xlarge" },
          style: ({ theme }) => ({
            ...theme.typography.buttonTextLarge,
            padding: `${pxToRem(4)} ${pxToRem(16)}`,
            height: "50px",
            minHeight: "50px",
            gap: "7px",
          }),
        },
        {
          props: { variant: "expressive" },
          style: ({ theme }) => ({
            background: theme.vars.palette.gradient.main,
            color: "white",
            lineHeight: pxToRem(26),
            "&:hover": {
              background: theme.vars.palette.gradient.light,
            },
            [`&.${buttonClasses.sizeLarge}`]: {
              paddingBottom: pxToRem(8),
              paddingLeft: pxToRem(30),
              paddingRight: pxToRem(30),
              paddingTop: pxToRem(8),
            },
          }),
        },
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedPrimaryBg,
            color: theme.vars.palette.Button.containedPrimaryContrastText,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedPrimaryHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedPrimaryActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "secondary" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedSecondaryBg,
            color: theme.vars.palette.Button.textPrimaryColor,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedSecondaryHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedSecondaryActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "error" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.containedErrorContrastText,
            backgroundColor: theme.vars.palette.Button.containedErrorBg,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedErrorHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedErrorActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "warning" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.containedWarningContrastText,
            backgroundColor: theme.vars.palette.Button.containedWarningBg,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedWarningHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedWarningActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "success" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.containedSuccessContrastText,
            backgroundColor: theme.vars.palette.Button.containedSuccessBg,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedSuccessHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedSuccessActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "info" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedPrimaryBg,
            color: theme.vars.palette.Button.containedPrimaryContrastText,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedPrimaryHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedPrimaryActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "inherit" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedSecondaryBg,
            color: theme.vars.palette.Button.containedInheritColor,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedSecondaryHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedSecondaryActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "automation" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedAutomationBg,
            color: theme.vars.palette.Button.containedAutomationContrastText,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedAutomationHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedAutomationActiveBg,
            },
          }),
        },
        {
          props: { variant: "contained", color: "white" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.sr25Gray[0],
            color: theme.vars.palette.sr25Gray[900],
            "&:hover": {
              backgroundColor: theme.vars.palette.sr25Gray[100],
            },
            "&:active": {
              backgroundColor: theme.vars.palette.sr25Gray[150],
            },
          }),
        },
        {
          // TODO: "Overlay" color is only used in EditCard.tsx.
          // Replace instance with another color then remove this color override.
          props: { variant: "contained", color: "overlay" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.Button.containedOverlayBg,
            color: theme.vars.palette.Button.containedOverlayContrastText,
            "&:hover": {
              backgroundColor: theme.vars.palette.Button.containedOverlayHoverBg,
            },
            "&:active": {
              backgroundColor: theme.vars.palette.Button.containedOverlayActiveBg,
            },
            [`&.${buttonClasses.disabled}`]: {
              "--variant-containedDisabledBg": theme.vars.palette.Button.containedOverlayDisabledBg,
              "--variant-containedDisabledColor":
                theme.vars.palette.Button.containedOverlayDisabledContrastText,
              backgroundColor: theme.vars.palette.Button.containedOverlayDisabledBg,
              color: theme.vars.palette.Button.containedOverlayDisabledContrastText,
              boxShadow: "none",
              opacity: 1,
            },
          }),
        },
        {
          props: { variant: "text", color: "primary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textPrimaryColor,
          }),
        },
        {
          props: { variant: "text", color: "secondary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textSecondaryColor,
          }),
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textPrimaryColor,
            borderColor: theme.vars.palette.Button.outlinedPrimaryBorder,
            "&:hover": {
              color: theme.vars.palette.Button.containedPrimaryContrastText,
              backgroundColor: theme.vars.palette.Button.containedPrimaryHoverBg,
              borderColor: theme.vars.palette.Button.containedPrimaryHoverBg,
            },
            "&:active": {
              color: theme.vars.palette.Button.containedPrimaryContrastText,
              backgroundColor: theme.vars.palette.Button.containedPrimaryActiveBg,
              borderColor: theme.vars.palette.Button.containedPrimaryActiveBg,
            },
          }),
        },
        {
          props: { variant: "outlined", color: "secondary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textPrimaryColor,
          }),
        },
      ],
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          textTransform: "none",
          "&:first-letter": {
            textTransform: "capitalize",
          },
        },
        sizeLarge: ({ theme }) => ({
          ...theme.typography.buttonTextLarge,
          padding: `${pxToRem(4)} ${pxToRem(16)}`,
          height: "42px",
          minHeight: "42px",
          gap: "7px",
        }),
        sizeMedium: ({ theme }) => ({
          ...theme.typography.buttonTextMedium,
          padding: `${pxToRem(4)} ${pxToRem(16)}`,
          height: "36px",
          minHeight: "36px",
          gap: "5px",
        }),
        sizeSmall: ({ theme }) => ({
          ...theme.typography.buttonTextSmall,
          borderRadius: theme.shape.borderRadiusSm,
          padding: `${pxToRem(4)} ${pxToRem(12)}`,
          height: "30px",
          minHeight: "30px",
          gap: "5px",
        }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: { fontSize: pxToRem(16) },
        sizeSmall: { fontSize: pxToRem(12) },
        sizeMedium: { fontSize: pxToRem(14) },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [`.${formLabelClasses.root} + .${formHelperTextClasses.root}, .MuiFormLabel-requiredContainer + .${formHelperTextClasses.root}`]:
            {
              marginTop: 0,
              marginBottom: theme.spacing(1),
            },
          [`.${formLabelClasses.root}:not(:has(+ .${formHelperTextClasses.root}))`]: {
            marginBottom: theme.spacing(1),
          },
          ".MuiFormLabel-requiredContainer": {
            [`&:not(:has(+ .${formHelperTextClasses.root}))`]: {
              marginBottom: theme.spacing(1),
            },
            ".MuiFormLabel-requiredIndicator": {
              margin: 0,
            },
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: theme.spacing(-0.5),
          gap: theme.spacing(0.5),
          [`.${formControlLabelClasses.label}`]: {
            fontSize: "0.875rem",
            lineHeight: 1.43,
          },
          "&.MuiFormControlLabel-medium": {
            [`.${formControlLabelClasses.label}`]: theme.typography.inputTextLarge,
          },
          "&.MuiFormControlLabel-large": {
            [`.${formControlLabelClasses.label}`]: theme.typography.inputTextLarge,
          },
          color: theme.vars.palette.text.primaryAlt,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          fontSize: pxToRem(13),
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          fontWeight: 500,
          color: theme.vars.palette.FormLabel.color,
          "&.Mui-focused:not(.Mui-error)": {
            color: theme.vars.palette.FormLabel.color,
          },
        }),
        asterisk: { display: "none" },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      variants: [
        {
          props: { color: "primary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textPrimaryColor,
            borderColor: theme.vars.palette.Button.outlinedPrimaryBorder,
            "&:hover": {
              color: theme.vars.palette.Button.containedPrimaryContrastText,
              backgroundColor: theme.vars.palette.Button.containedPrimaryHoverBg,
              borderColor: theme.vars.palette.Button.containedPrimaryHoverBg,
            },
            "&:active": {
              color: theme.vars.palette.Button.containedPrimaryContrastText,
              backgroundColor: theme.vars.palette.Button.containedPrimaryActiveBg,
              borderColor: theme.vars.palette.Button.containedPrimaryActiveBg,
            },
          }),
        },
        {
          props: { color: "secondary" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textPrimaryColor,
          }),
        },
        {
          props: { color: "subtle" },
          style: ({ theme }) => ({
            color: theme.vars.palette.Button.textSecondaryColor,
            opacity: 0.5,
            "&:hover": {
              opacity: 1,
            },
          }),
        },
      ],
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.on.surfaceDimmer,
        }),
        sizeLarge: {
          padding: `${pxToRem(4)} ${pxToRem(16)}`,
          height: "42px",
          width: "42px",
          [`& .${svgIconClasses.root}`]: {
            fontSize: pxToRem(20),
          },
        },
        sizeMedium: {
          padding: `${pxToRem(4)} ${pxToRem(16)}`,
          height: "36px",
          width: "36px",
          [`& .${svgIconClasses.root}`]: {
            fontSize: pxToRem(18),
          },
        },
        sizeSmall: {
          padding: `${pxToRem(4)} ${pxToRem(12)}`,
          height: "30px",
          width: "30px",
          [`& .${svgIconClasses.root}`]: {
            fontSize: pxToRem(16),
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        size: "medium",
        checkedIcon: <FaIcon icon={faCircle} fontSize="inherit" />,
        icon: <FaIcon icon={faCircle} fontSize="inherit" sx={{ opacity: 0 }} />,
      },
      variants: [
        {
          props: { readOnly: true },
          style: ({ theme }) => {
            const selectors = getSelectionControlSelectors({
              checkedClasses: [radioClasses.checked],
              disabledClass: radioClasses.disabled,
              rootClass: radioClasses.root,
            });

            return {
              cursor: "default",

              [selectors.hover]: {
                backgroundColor: "transparent",
                borderColor: theme.vars.palette.stroke.main,
              },

              [selectors.active]: {
                borderColor: theme.vars.palette.stroke.main,
              },

              [selectors.checkedActive]: {
                backgroundColor: theme.vars.palette.primary.main,
                borderColor: theme.vars.palette.primary.main,
              },
            };
          },
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({
            width: "16px",
            height: "16px",
            fontSize: pxToRem(6),
            [theme.breakpoints.down("sm")]: {
              width: "20px",
              height: "20px",
              fontSize: pxToRem(8),
            },
          }),
        },
        {
          props: { size: "large" },
          style: ({ theme }) => ({
            width: "24px",
            height: "24px",
            fontSize: pxToRem(10),

            [theme.breakpoints.down("sm")]: {
              width: "28px",
              height: "28px",
              fontSize: pxToRem(12),
            },
          }),
        },
      ],
      styleOverrides: {
        root: ({ theme }) => {
          const selectors = getSelectionControlSelectors({
            checkedClasses: [radioClasses.checked],
            disabledClass: radioClasses.disabled,
            rootClass: radioClasses.root,
          });

          return {
            color: theme.vars.palette.Checkbox.color,
            borderRadius: theme.vars.shape.borderRadiusMax,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            margin: 5,
            border: `1px solid ${theme.vars.palette.stroke.main}`,
            width: "20px",
            height: "20px",
            fontSize: pxToRem(8),
            "--transition-property-override": "border-color",

            [theme.breakpoints.down("sm")]: {
              width: "24px",
              height: "24px",
              fontSize: pxToRem(10),
            },

            [selectors.focusVisible]: {
              outline: `2px solid oklch(${sr25BlueOklch[500]} / 0.5)`,
              outlineOffset: "1px",
            },

            [selectors.checked]: {
              backgroundColor: theme.vars.palette.primary.main,
              borderColor: theme.vars.palette.primary.main,
              color: theme.vars.palette.on.primary,

              [`& + .${formControlLabelClasses.label}`]: {
                color: theme.vars.palette.Checkbox.checkedLabelColor,
              },
            },

            [selectors.interaction]: {
              "--transition-duration-override": `${theme.transitions.duration.interactiveStateIn}ms`,
            },

            [selectors.hover]: {
              backgroundColor: "transparent",
              borderColor: theme.vars.palette.stroke.hover,
            },

            [selectors.active]: {
              borderColor: theme.vars.palette.stroke.active,
            },

            [selectors.checkedHover]: {
              backgroundColor: theme.vars.palette.primary.main,
              borderColor: theme.vars.palette.primary.main,
            },

            [selectors.checkedActive]: {
              backgroundColor: theme.vars.palette.primary.dark,
              borderColor: theme.vars.palette.primary.dark,
            },

            [selectors.checkedDisabledHover]: {
              borderColor: theme.vars.palette.primary.main,
            },

            [selectors.disabled]: {
              opacity: 0.5,
              cursor: "default",
            },
          };
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: "medium",
      },
      styleOverrides: {
        root: ({ theme }) => {
          const largeSizeConfig = {
            width: 39,
            height: 24,
            padding: 1,
            thumbSize: 22,
            checkedTranslateX: 15,
          };
          const mediumSizeConfig = {
            width: 32,
            height: 20,
            padding: 1,
            thumbSize: 18,
            checkedTranslateX: 12,
          };
          const smallSizeConfig = {
            width: 26,
            height: 16,
            padding: 1,
            thumbSize: 14,
            checkedTranslateX: 10,
          };
          const getSwitchSizeStyles = (sizeConfig: typeof largeSizeConfig) => ({
            width: sizeConfig.width,
            height: sizeConfig.height,
            [`& .${switchClasses.switchBase}`]: {
              padding: sizeConfig.padding,
              [`&.${switchClasses.checked}`]: {
                transform: `translateX(${sizeConfig.checkedTranslateX}px)`,
              },
            },
            [`& .${switchClasses.thumb}`]: {
              width: sizeConfig.thumbSize,
              height: sizeConfig.thumbSize,
            },
          });
          const primaryCheckedTrackColor = {
            default: theme.vars.palette.Switch.trackOnPrimaryBg,
            hover: theme.vars.palette.Switch.trackOnPrimaryHoverBg,
            active: theme.vars.palette.Switch.trackOnPrimaryActiveBg,
          };
          const successCheckedTrackColor = {
            default: theme.vars.palette.Switch.trackOnSuccessBg,
            hover: theme.vars.palette.Switch.trackOnSuccessHoverBg,
            active: theme.vars.palette.Switch.trackOnSuccessActiveBg,
          };
          const uncheckedTrackColor = {
            bg: {
              default: theme.vars.palette.Switch.trackOffBg,
              hover: theme.vars.palette.Switch.trackOffHoverBg,
              active: theme.vars.palette.Switch.trackOffActiveBg,
            },
            border: {
              default: theme.vars.palette.Switch.trackOffBorder,
              hover: theme.vars.palette.Switch.trackOffHoverBorder,
              active: theme.vars.palette.Switch.trackOffActiveBorder,
            },
          };

          return {
            width: largeSizeConfig.width,
            height: largeSizeConfig.height,
            padding: 0,
            overflow: "visible",
            opacity: 1,
            [`& .${switchClasses.switchBase}`]: {
              padding: largeSizeConfig.padding,
              color: theme.vars.palette.Switch.thumbColor,
              "&:hover": {
                backgroundColor: "transparent",
                [`& + .${switchClasses.track}`]: {
                  backgroundColor: uncheckedTrackColor.bg.hover,
                  borderColor: uncheckedTrackColor.border.hover,
                },
              },
              "&:active": {
                backgroundColor: "transparent",
                [`& + .${switchClasses.track}`]: {
                  backgroundColor: uncheckedTrackColor.bg.active,
                  borderColor: uncheckedTrackColor.border.active,
                },
              },
              [`&.Mui-focusVisible + .${switchClasses.track}`]: {
                boxShadow: `0 0 0 ${theme.vars.palette.Switch.focusRingWidth} ${theme.vars.palette.Switch.focusRingColor}`,
              },
              [`&.${switchClasses.checked}`]: {
                transform: `translateX(${largeSizeConfig.checkedTranslateX}px)`,
                color: theme.vars.palette.Switch.thumbColor,
                [`& + .${switchClasses.track}`]: {
                  opacity: 1,
                  backgroundColor: primaryCheckedTrackColor.default,
                  borderColor: primaryCheckedTrackColor.default,
                },
                [`&:hover + .${switchClasses.track}`]: {
                  backgroundColor: primaryCheckedTrackColor.hover,
                  borderColor: primaryCheckedTrackColor.hover,
                },
                [`&:active + .${switchClasses.track}`]: {
                  backgroundColor: primaryCheckedTrackColor.active,
                  borderColor: primaryCheckedTrackColor.active,
                },
              },
            },
            [`& .${switchClasses.thumb}`]: {
              width: largeSizeConfig.thumbSize,
              height: largeSizeConfig.thumbSize,
              boxSizing: "border-box",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.24)",
            },
            [`& .${switchClasses.switchBase}.${switchClasses.disabled} .${switchClasses.thumb}`]: {
              backgroundColor: theme.vars.palette.Switch.thumbColor,
            },
            [`& .${switchClasses.track}`]: {
              borderRadius: 100,
              boxSizing: "border-box",
              border: `1px solid ${uncheckedTrackColor.border.default}`,
              backgroundColor: uncheckedTrackColor.bg.default,
              opacity: 1,
              transitionProperty: "opacity, background-color, border-color",
            },
            [`& .${switchClasses.switchBase}.${switchClasses.disabled} + .${switchClasses.track}`]:
              {
                opacity: 1,
              },
            variants: [
              {
                props: { size: "small" },
                style: getSwitchSizeStyles(smallSizeConfig),
              },
              {
                props: { size: "medium" },
                style: getSwitchSizeStyles(mediumSizeConfig),
              },
              {
                props: { disabled: true },
                style: {
                  opacity: 0.5,
                },
              },
              {
                props: { color: "success" },
                style: {
                  [`& .${switchClasses.switchBase}.${switchClasses.checked}`]: {
                    [`& + .${switchClasses.track}`]: {
                      opacity: 1,
                      backgroundColor: successCheckedTrackColor.default,
                      borderColor: successCheckedTrackColor.default,
                    },
                    [`&:hover + .${switchClasses.track}`]: {
                      backgroundColor: successCheckedTrackColor.hover,
                      borderColor: successCheckedTrackColor.hover,
                    },
                    [`&:active + .${switchClasses.track}`]: {
                      backgroundColor: successCheckedTrackColor.active,
                      borderColor: successCheckedTrackColor.active,
                    },
                  },
                },
              },
            ],
          };
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: (props) => <FaIcon icon={faChevronDown} {...props} />,
      },
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          height: "auto",
          cursor: "pointer",
        },
        select: ({ theme }) => ({
          color: theme.vars.palette.Select.color,
          // MuiOutlinedInput applies generic input padding through a
          // three-class descendant selector. Duplicate the resolved Select
          // selector so Select-specific padding wins without relying on order.
          "&&": {
            paddingTop: "var(--select-padding-block)",
            // Preserve clearance for the absolutely positioned chevron. The
            // generic OutlinedInput size variants otherwise replace MUI's
            // Select-specific right padding with ordinary input padding.
            paddingRight: theme.spacing(4),
            paddingBottom: "var(--select-padding-block)",
          },
        }),
        icon: ({ theme }) => ({
          fontSize: "0.75rem",
          color: theme.vars.palette.on.surfaceDimmer,
          right: "16px",
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          minHeight: pxToRem(42),
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          variants: getInputBaseSizeVariants(theme, ({ typography }) => ({
            ...typography,
          })),
        }),
        inputMultiline: ({ theme }) => ({
          variants: getInputBaseSizeVariants(theme, ({ typography }) => ({
            ...typography,
            minHeight: "0",
          })),
        }),
        root: ({ theme }) => ({
          [`&.${inputBaseClasses.multiline} > .${inputBaseClasses.input}`]: {
            lineHeight: "1.5",
          },
          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
            display: "none",
          },
          "& input[type=number]": {
            MozAppearance: "textfield",
          },
          [`&.${inputBaseClasses.root}`]: {
            [`&, & .${outlinedInputClasses.notchedOutline}`]: {
              transitionProperty:
                "var(--transition-property-override, color, border-color, box-shadow, background-color)",
              transitionDuration: `var(--transition-duration-override, ${theme.transitions.duration.interactiveStateOut}ms)`,
            },
            [`&:hover, &.${inputBaseClasses.focused}`]: {
              [`&, & .${outlinedInputClasses.notchedOutline}`]: {
                transitionDuration: `var(--transition-duration-override, ${theme.transitions.duration.interactiveStateIn}ms)`,
              },
            },
          },
          variants: getInputBaseSizeVariants(
            theme,
            ({ minHeight, paddingTop, paddingBottom, selectPaddingBlock }) => ({
              minHeight,

              [`&:not(.${inputBaseClasses.multiline}):not(.${selectClasses.root}):not(.${autocompleteClasses.inputRoot})`]:
                {
                  height: minHeight,
                },
              // Select does not inherit a FormControl size into its own owner state, so expose the padding where InputBase has resolved it.
              "--select-padding-block": selectPaddingBlock,
              // Absolutely all of these selectors are needed to override the default MUI styles for OutlinedInput and FilledInput.
              [`&.${inputBaseClasses.multiline} > .${inputBaseClasses.input}.MuiInputBase-inputMultiline`]:
                {
                  paddingTop: paddingTop,
                  paddingBottom: paddingBottom,
                },
            }),
          ),
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: ({ theme }) => ({
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          color: theme.vars.palette.on.surfaceDimmer,
          margin: "0",
          gap: theme.spacing(0.5),
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.vars.palette.FilledInput.bg,
          [`& > .${inputBaseClasses.input}`]: {
            color: theme.vars.palette.FilledInput.color,
            "&::placeholder": {
              color: theme.vars.palette.FilledInput.placeholderColor,
              opacity: theme.vars.palette.FilledInput.placeholderOpacity,
            },
          },
          [`&.${inputBaseClasses.multiline}`]: {
            padding: "0",
          },
          [`&:hover, &.${inputBaseClasses.focused}`]: {
            backgroundColor: theme.vars.palette.FilledInput.hoverBg,
          },
          [`&.${inputBaseClasses.disabled}`]: {
            backgroundColor: theme.vars.palette.FilledInput.disabledBg,
          },
          variants: [
            {
              props: { size: "small" },
              // Note: these are the same base styles as OutlinedInput, but we need to repeat them here
              // to ensure these styles override the default MUI styles for FilledInput.
              style: {
                [`&.${inputBaseClasses.root}`]: {
                  padding: `0 ${theme.spacing(0.75)}`,
                  [`& > .${inputBaseClasses.input}`]: {
                    padding: `0 ${theme.spacing(0.75)}`,
                  },
                },
              },
            },
            {
              props: { size: "medium" },
              // Note: these are the same base styles as OutlinedInput, but we need to repeat them here
              // to ensure these styles override the default MUI styles for FilledInput.
              style: {
                [`&.${inputBaseClasses.root}`]: {
                  padding: `0 ${theme.spacing(1)}`,
                  [`& > .${inputBaseClasses.input}`]: {
                    padding: `0 ${theme.spacing(1)}`,
                  },
                },
              },
            },
            {
              props: { readOnly: true },
              style: {
                "&:hover, &:focus-within": {
                  backgroundColor: theme.vars.palette.FilledInput.bg,
                },
              },
            },
          ],
          ":before, :after": { content: "none" },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          color: theme.vars.palette.OutlinedInput.color,
          boxShadow: theme.vars.palette.OutlinedInput.boxShadow,
          backgroundColor: theme.vars.palette.OutlinedInput.bg,
          [`& > .${inputBaseClasses.input}`]: {
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            color: theme.vars.palette.OutlinedInput.color,
            "&::placeholder": {
              color: theme.vars.palette.OutlinedInput.placeholderColor,
              opacity: theme.vars.palette.OutlinedInput.placeholderOpacity,
            },
          },
          [`&.${inputBaseClasses.multiline}`]: {
            padding: "0",
          },
          fieldset: {
            borderColor: theme.vars.palette.OutlinedInput.border,
          },
          [`&.${inputBaseClasses.disabled}`]: {
            backgroundColor: theme.vars.palette.OutlinedInput.disabledBg,

            fieldset: {
              borderColor: `${theme.vars.palette.OutlinedInput.disabledBorder}`,
            },
          },
          [`&:hover:not(.${inputBaseClasses.focused}):not(.${inputBaseClasses.disabled}):not(.${inputBaseClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
            {
              borderColor: theme.vars.palette.OutlinedInput.hoverBorder,
            },
          [`&.${inputBaseClasses.focused}:not(.${inputBaseClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
            {
              borderWidth: "1px",
              borderColor: theme.vars.palette.OutlinedInput.focusBorder,
            },
          variants: [
            {
              props: { size: "small" },
              // Note: these are the same base styles as FilledInput, but we need to repeat them here
              // to ensure these styles override the default MUI styles for OutlinedInput.
              style: {
                [`&.${inputBaseClasses.root}`]: {
                  padding: `0 ${theme.spacing(0.75)}`,
                  [`& > .${inputBaseClasses.input}`]: {
                    padding: `0 ${theme.spacing(0.75)}`,
                  },
                },
              },
            },
            {
              props: { size: "medium" },
              // Note: these are the same base styles as FilledInput, but we need to repeat them here
              // to ensure these styles override the default MUI styles for OutlinedInput.
              style: {
                [`&.${inputBaseClasses.root}`]: {
                  padding: `0 ${theme.spacing(1)}`,
                  [`& > .${inputBaseClasses.input}`]: {
                    padding: `0 ${theme.spacing(1)}`,
                  },
                },
              },
            },
            {
              props: { readOnly: true },
              style: {
                backgroundColor: "transparent",
                boxShadow: "none",

                [`& .${outlinedInputClasses.notchedOutline}, &:hover .${outlinedInputClasses.notchedOutline}, &.${inputBaseClasses.focused} .${outlinedInputClasses.notchedOutline}, &.${inputBaseClasses.error} .${outlinedInputClasses.notchedOutline}, &.${inputBaseClasses.disabled} .${outlinedInputClasses.notchedOutline}`]:
                  {
                    borderColor: "transparent",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  },
              },
            },
          ],
        }),
      },
    },
    MuiTable: {
      variants: [
        {
          props: {},
          style: ({ theme }) => ({
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              "&:first-of-type": {
                borderTopLeftRadius: theme.shape.borderRadius,
                borderBottomLeftRadius: theme.shape.borderRadius,
              },
              "&:last-of-type": {
                borderTopRightRadius: theme.shape.borderRadius,
                borderBottomRightRadius: theme.shape.borderRadius,
              },
            },
            [`& .${tableCellClasses.head}`]: {
              fontWeight: 400,
              color: theme.vars.palette.text.secondary,
            },
            [`& td.${tableCellClasses.body}`]: {
              // `td` treats "height" like "min-height"
              height: "60px",
            },
          }),
        },
        {
          props: { size: "small" },
          style: {
            [`& td.${tableCellClasses.body}, th.${tableCellClasses.head}`]: {
              // `td` treats "height" like "min-height"
              height: "42px",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontSize: pxToRem(15),
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          color: theme.vars.palette.Slider.primaryTrack,
        }),
        rail: ({ theme }) => ({
          color: theme.vars.palette.Slider.railColor,
        }),
        mark: ({ theme }) => ({
          height: 3,
          width: 3,
          backgroundColor: theme.vars.palette.Slider.markBg,
        }),
        root: ({ theme }) => ({
          ".MuiSlider-markActive": {
            backgroundColor: theme.vars.palette.Slider.bg,
          },
        }),
        track: {
          border: "none",
        },
        valueLabel: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Slider.valueLabelBg,
        }),
      },
      variants: [
        {
          props: { size: "medium" },
          style: {
            height: "6px",
          },
        },
      ],
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.getColorSchemeSelector("dark")]: {
            color: dark.red[50],
            "& .MuiAlert-filledError": {
              backgroundColor: dark.red[800],
              color: "inherit",
              "& .MuiAlert-message > :last-child": {
                color: dark.red[300],
              },
            },
          },
        }),
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          cursor: "pointer",
          "&.Mui-active": {
            borderStyle: "solid",
            borderWidth: "2px",
            borderRadius: "50%",
          },
          "&&": {
            fontSize: "1.5rem",
          },

          [theme.getColorSchemeSelector("dark")]: {
            color: dark.grey[50],
            "&.Mui-active": {
              color: "transparent",
              borderColor: dark.grey[700],
            },
            "&.Mui-completed": {
              "&.Mui-active": {
                color: dark.grey[50],
                backgroundColor: dark.green[500],
              },
              color: dark.green[500],
              borderColor: dark.green[500],
            },
          },
        }),
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          cursor: "pointer",

          [theme.getColorSchemeSelector("dark")]: {
            color: dark.grey[700],
            fontWeight: 500,
            "&.Mui-active": {
              color: dark.grey.A900,
            },
            "&.Mui-completed": {
              color: dark.grey[700],
            },
          },
        }),
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderLeftStyle: "solid",
          borderLeftWidth: "2px",
          [theme.getColorSchemeSelector("dark")]: {
            color: dark.grey[600],
            borderLeftColor: dark.grey[300],
          },
        }),
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: ({ theme }) => ({
          borderLeftStyle: "solid",
          borderLeftWidth: "2px",
          [theme.getColorSchemeSelector("dark")]: {
            borderColor: dark.grey["300"],
          },
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          color: theme.vars.palette.SvgIcon.primaryColor,
        }),
        root: ({ theme, ownerState }) => ({
          fontSize: getSvgIconFontSize(theme, ownerState.fontSize),
          ...(ownerState.color === "success" && {
            color: theme.vars.palette.SvgIcon.successColor,
          }),
          ...(ownerState.color === "error" && {
            color: theme.vars.palette.SvgIcon.errorColor,
          }),
          ...(ownerState.color === "automation" && {
            color: theme.vars.palette.SvgIcon.automationColor,
          }),
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: "relative",
          "& .MuiTabs-indicator": {
            borderRadius: "1.5px",
            height: "3px",
          },
          "& .MuiTabs-flexContainer": {
            gap: "0.5rem",
          },
          "& .MuiTabs-flexContainer:after": {
            backgroundColor: theme.vars.palette.Tabs.trackColor,
          },
          "&:before": {
            content: '""',
            height: "3px",
            backgroundColor: theme.vars.palette.Tabs.trackColor,
            borderRadius: "1.5px",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          marginBottom: "0.5rem",
          borderRadius: theme.shape.borderRadius,
          textTransform: "none",
          fontWeight: 500,
          color: theme.vars.palette.Tab.color,
          "&:hover": {
            backgroundColor: theme.vars.palette.Tab.hoverBg,
          },
          "&:active": {
            backgroundColor: theme.vars.palette.Tab.activeBg,
          },
          "&.Mui-selected": {
            fontWeight: 600,
            color: theme.vars.palette.Tab.selectedColor,
            backgroundColor: theme.vars.palette.Tab.selectedBg,
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.${tableRowClasses.hover}:hover`]: {
            backgroundColor: theme.vars.palette.Table.rowHoverBg,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(15),
        },
        head: ({ theme }) => ({
          backgroundColor: theme.vars.palette.Table.headBg,
        }),
        body: ({ theme }) => ({
          color: theme.vars.palette.Table.bodyColor,
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: "top",
        enterTouchDelay: 0,
        leaveTouchDelay: 3000,
      },
      styleOverrides: {
        arrow: ({ theme }) => ({
          color: theme.vars.palette.Tooltip.bg,
        }),
        tooltip: ({ theme }) => ({
          ...typography.body2,
          padding: 16,
          marginLeft: 10,
          marginRight: 10,
          [theme.breakpoints.up("sm")]: {
            ...typography.body1,
            paddingBottom: 16,
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 16,
          },
          backgroundColor: theme.vars.palette.Tooltip.bg,
          color: theme.vars.palette.Tooltip.color,
          boxShadow: theme.vars.palette.Tooltip.boxShadow,
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          h5: "h5",
          h6: "h6",
          subtitle1: "p",
          subtitle2: "p",
          body1: "p",
          body2: "p",
          inherit: "p",
          overline: "p",
          helperText: "p",
          caption: "p",
          inputLabel: "span",
          inputText: "span",
          inputTextLarge: "span",
          buttonTextLarge: "span",
          buttonTextMedium: "span",
          buttonTextSmall: "span",
        },
      },
      styleOverrides: {
        root: {
          [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
        },
      },
      variants: [
        {
          props: { variant: "overline" },
          style: ({ theme }) => ({
            color: theme.vars.palette.automation.dark,
          }),
        },
        {
          props: { color: "gradient" },
          style: ({ theme }) => ({
            background: theme.vars.palette.gradient.main,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }),
        },
        {
          props: { color: "gradient.blueToPurple" },
          style: ({ theme }) => ({
            background: theme.vars.palette.gradient.blueToPurple,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }),
        },
      ],
    },
  },
});
